import React from 'react';
import {useGetCountriesQuery} from '../store/api/settingsApi';
import {messageType, showMessages} from '../helpers/message';
import {BaseError} from '../types/apiTypes';

/**
 * Memoize countries into accessible objects. Have provinces dynamically update based on the country index that is assigned.
 */
const useGetCountries = (currentCountry?: string) => {
    const initRef = React.useRef(false);
    const {data: countriesData, isLoading: countriesLoading, isError, error} = useGetCountriesQuery();

    // Currently, the province index state is not used/needed, but might be beneficial in the future.
    const [currentIndex, setCurrentIndex] = React.useState<{country: number | null, province: number | null}>({
        country: null, province: null // Have initial state start null if DL form is fresh/empty.
    });

    // Memoize countries with unique index for easy reference.
    const countries = React.useMemo(() => {
        return countriesData?.map((country, index) => {
            return {key: index, value: country.name}
        });
    }, [countriesData]);

    // Memoize provinces/states dynamically on currentCountry changing.
    const provinces = React.useMemo(() => {
        // Provinces start undefined. If the country has no states will return an empty array.
        if (countriesData && currentIndex.country !== null) {
            return countriesData[currentIndex.country]?.states.map((state, index) => {
                return {key: index, value: state.name};
            });
        }
    }, [countriesData, currentIndex.country]);

    React.useEffect(() => {
        // On initial render, set up the countryIndex to trigger assigning respective provinces.
        if (initRef.current || !countriesData || countriesLoading) return;
        if (currentCountry && countries) {
            const countryIndex = countries.findIndex((country) => {
                return currentCountry === country.value;
            });
            setCurrentIndex({country: countryIndex, province: null});
            initRef.current = true;
        }
    }, [countries, countriesData, countriesLoading, currentCountry]);

    React.useEffect(() => {
        if (isError) {
            const errorMessage = (error as BaseError)?.data?.message ?? "There was an issue fetching country/province data. Please refresh the page.";
            showMessages(errorMessage, messageType.error);
        }
    }, [error, isError]);

    return {
        countriesLoading,
        countries,
        provinces,
        setCurrentIndex,
    }
}

export {useGetCountries};
